import GeneralUtils from "../../shared/utils/GeneralUtils";

export const MENU_ICON_GBP = 'dashboard-menu-icon--gbp';
export const MENU_ICON_QR = 'dashboard-menu-icon--qr';

export function isGbpMenuIcon(iconClass) {
    return iconClass === MENU_ICON_GBP;
}

export function isQrMenuIcon(iconClass) {
    return iconClass === MENU_ICON_QR;
}

export function compareMenuByDescription(a, b) {
    const labelA = (a && a.description != null ? String(a.description) : '').trim();
    const labelB = (b && b.description != null ? String(b.description) : '').trim();
    return labelA.localeCompare(labelB, undefined, {sensitivity: 'base'});
}

const INVENTORY_MANAGEMENT_SECTION_PATTERN = /inventory\s+management/i;

export function isInventoryManagementSection(parent) {
    return parent != null && INVENTORY_MANAGEMENT_SECTION_PATTERN.test(String(parent.description || ''));
}

function isInvoiceMenu(item) {
    return /^invoice$/i.test((item?.description || '').trim());
}

export function sortInventoryManagementMenus(menus) {
    if (!Array.isArray(menus) || menus.length === 0) {
        return [];
    }
    return [...menus].sort((a, b) => {
        const aIsInvoice = isInvoiceMenu(a);
        const bIsInvoice = isInvoiceMenu(b);
        if (aIsInvoice && !bIsInvoice) {
            return -1;
        }
        if (!aIsInvoice && bIsInvoice) {
            return 1;
        }
        return compareMenuByDescription(a, b);
    });
}

export function sortMenusForSection(parent, menus) {
    if (!Array.isArray(menus) || menus.length === 0) {
        return [];
    }
    if (isInventoryManagementSection(parent)) {
        return sortInventoryManagementMenus(menus);
    }
    return sortMenusByDescription(menus);
}

export function sortMenusByDescription(menus) {
    if (!Array.isArray(menus) || menus.length === 0) {
        return [];
    }
    return [...menus].sort(compareMenuByDescription);
}

/** API may return a raw array or a wrapped payload — always coerce to an array before .filter */
export function normalizeMenuListResponse(data) {
    if (Array.isArray(data)) {
        return data;
    }
    if (data != null && typeof data === 'object') {
        if (Array.isArray(data.data)) {
            return data.data;
        }
        if (Array.isArray(data.menus)) {
            return data.menus;
        }
        if (Array.isArray(data.menuList)) {
            return data.menuList;
        }
        if (Array.isArray(data.items)) {
            return data.items;
        }
    }
    return [];
}

export function isMerchantMenuApiSuccess(data) {
    if (GeneralUtils.isNullOrEmpty(data)) {
        return false;
    }
    if (data != null && typeof data === 'object' && !GeneralUtils.isNullOrEmpty(data.type)) {
        return false;
    }
    return Array.isArray(data) || normalizeMenuListResponse(data).length > 0;
}

/**
 * Merchant menu ids from Keycloak roles_menu.json, data.sql, and branch extensions.
 * PrimeIcons 2.x only — artpos glyphicon equivalents noted in comments.
 */
export const MENU_ICON_BY_ID = {
    101: 'pi pi-list',           // Web Menu (artpos: menu-hamburger)
    102: 'pi pi-copy',           // Product Groups (artpos: duplicate)
    103: 'pi pi-tag',            // Products (artpos: barcode)
    104: 'pi pi-tags',           // Product Tag (artpos: tag)
    105: 'pi pi-folder-open',    // Departments (artpos: folder-open)
    108: 'pi pi-briefcase',      // Supplier (artpos: briefcase)
    109: MENU_ICON_GBP, // Product Price (£)
    201: 'pi pi-users',          // Users (artpos: user)
    203: 'pi pi-th-large',       // Floor Planning (artpos: th-large)
    204: 'pi pi-file',           // Taxes (artpos: list-alt)
    301: 'pi pi-shopping-cart',  // Open Orders
    302: 'pi pi-calendar',       // Order History
    401: 'pi pi-chart-bar',      // Sale Report (artpos: stats)
    402: 'pi pi-chart-bar',      // Admin Sale Report
    403: 'pi pi-chart-line',     // Summary Report
    501: 'pi pi-cog',            // Branch Settings (artpos: cog)
    502: 'pi pi-user',           // Customer (artpos: user)
    503: 'pi pi-clone',          // Mix And Match (artpos: random)
    601: 'pi pi-id-card',        // Merchant Accounts
    602: 'pi pi-user-edit',      // Admin Users
    701: 'pi pi-envelope',       // Mail
    702: 'pi pi-key',            // Licenses
    703: 'pi pi-upload',         // Data (artpos: folder-open export)
    704: MENU_ICON_QR,           // Qr Code
    705: 'pi pi-desktop',        // Till Connections (artpos: hdd)
    801: 'pi pi-inbox',          // Stock Ins & Outs
    9: 'pi pi-shopping-cart',    // Purchase order group (mobile filter)
    903: 'pi pi-shopping-cart',  // Purchase Order mobile
    904: 'pi pi-list',           // Purchase Requisition mobile
};

/** Normalized hash path (no #, lowercase) → icon */
export const MENU_ICON_BY_HREF = {
    webmenulist: 'pi pi-list',
    webmenusave: 'pi pi-list',
    productgroupsave: 'pi pi-copy',
    productlist: 'pi pi-tag',
    productsave: 'pi pi-tag',
    producttagsave: 'pi pi-tags',
    departmentlist: 'pi pi-folder-open',
    departmentsave: 'pi pi-folder-open',
    supplier: 'pi pi-briefcase',
    depossupplier: 'pi pi-briefcase',
    supplierproduct: 'pi pi-tags',
    merchantusersave: 'pi pi-users',
    floorplanning: 'pi pi-th-large',
    taxsave: 'pi pi-file',
    orders: 'pi pi-shopping-cart',
    orderhistory: 'pi pi-calendar',
    salereport: 'pi pi-chart-bar',
    adminsalereport: 'pi pi-chart-bar',
    reportsummary: 'pi pi-chart-line',
    branchsettings: 'pi pi-cog',
    eodsettings: 'pi pi-cog',
    customer: 'pi pi-user',
    customersave: 'pi pi-user',
    mixandmatchlist: 'pi pi-clone',
    mixandmatchsave: 'pi pi-clone',
    merchantaccountlist: 'pi pi-id-card',
    merchantaccountsave: 'pi pi-id-card',
    merchantbranchsave: 'pi pi-map-marker',
    adminuserlist: 'pi pi-user-edit',
    mailsettings: 'pi pi-envelope',
    license: 'pi pi-key',
    licenses: 'pi pi-key',
    dataupload: 'pi pi-upload',
    barcodegenerator: MENU_ICON_QR,
    tillconnections: 'pi pi-desktop',
    stockinouts: 'pi pi-inbox',
    productpricelist: MENU_ICON_GBP,
    purchaseorder: 'pi pi-shopping-cart',
    purchaseordersave: 'pi pi-shopping-cart',
    purchaseordermobile: 'pi pi-mobile',
    purchaseordermobilesave: 'pi pi-mobile',
    purchaserequisition: 'pi pi-list',
    purchaserequisitionsave: 'pi pi-list',
    purchaserequisitionmobile: 'pi pi-mobile',
    purchaserequisitionsavemobile: 'pi pi-mobile',
    ordersdisplay: 'pi pi-eye',
    ordersforkitchen: 'pi pi-eye',
};

const MENU_ICON_ALIASES = {
    'pi-building': 'pi pi-id-card',
    'pi-box': 'pi pi-tag',
    'pi-truck': 'pi pi-briefcase',
    'pi-qrcode': MENU_ICON_QR,
};

const MENU_DESCRIPTION_RULES = [
    {pattern: /web menu/i, icon: 'pi pi-list'},
    {pattern: /product group/i, icon: 'pi pi-copy'},
    {pattern: /product tag/i, icon: 'pi pi-tags'},
    {pattern: /product price/i, icon: MENU_ICON_GBP},
    {pattern: /products?/i, icon: 'pi pi-tag'},
    {pattern: /department/i, icon: 'pi pi-folder-open'},
    {pattern: /supplier/i, icon: 'pi pi-briefcase'},
    {pattern: /floor/i, icon: 'pi pi-th-large'},
    {pattern: /tax/i, icon: 'pi pi-file'},
    {pattern: /open order/i, icon: 'pi pi-shopping-cart'},
    {pattern: /order history/i, icon: 'pi pi-calendar'},
    {pattern: /summary report/i, icon: 'pi pi-chart-line'},
    {pattern: /sale report/i, icon: 'pi pi-chart-bar'},
    {pattern: /report/i, icon: 'pi pi-chart-bar'},
    {pattern: /branch settings/i, icon: 'pi pi-cog'},
    {pattern: /customer/i, icon: 'pi pi-user'},
    {pattern: /mix.*match/i, icon: 'pi pi-clone'},
    {pattern: /\binvoice\b/i, icon: 'pi pi-file-pdf'},
    {pattern: /purchase/i, icon: 'pi pi-shopping-cart'},
    {pattern: /stock/i, icon: 'pi pi-inbox'},
    {pattern: /mail/i, icon: 'pi pi-envelope'},
    {pattern: /licen/i, icon: 'pi pi-key'},
    {pattern: /data/i, icon: 'pi pi-upload'},
    {pattern: /qr\s*code|\bqr\b/i, icon: MENU_ICON_QR},
    {pattern: /barcode\s*generator/i, icon: MENU_ICON_QR},
    {pattern: /till/i, icon: 'pi pi-desktop'},
    {pattern: /merchant account/i, icon: 'pi pi-id-card'},
    {pattern: /admin user/i, icon: 'pi pi-user-edit'},
    {pattern: /users?/i, icon: 'pi pi-users'},
    {pattern: /kitchen|display/i, icon: 'pi pi-eye'},
];

function normalizeHrefKey(href) {
    if (GeneralUtils.isNullOrEmpty(href)) {
        return '';
    }
    return String(href).replace(/^#\/?/i, '').replace(/\?.*$/, '').toLowerCase();
}

function normalizeIconClass(icon) {
    if (GeneralUtils.isNullOrEmpty(icon)) {
        return null;
    }
    let cls = String(icon).trim();
    if (!cls) {
        return null;
    }
    if (!cls.startsWith('pi')) {
        cls = 'pi pi-' + cls.replace(/^pi-?/, '');
    } else if (!cls.startsWith('pi ')) {
        cls = cls.replace(/^pi-/, 'pi pi-');
    }
    const base = cls.split(/\s+/).find(t => t.startsWith('pi-') && t !== 'pi-fw');
    if (base === 'pi-qrcode') {
        return MENU_ICON_QR;
    }
    if (base && MENU_ICON_ALIASES[base]) {
        return MENU_ICON_ALIASES[base];
    }
    return cls;
}

export function resolveMenuIcon(item) {
    if (GeneralUtils.isNullOrEmpty(item)) {
        return 'pi pi-th-large';
    }

    const description = (item.description || '').trim();
    // Invoice shares #/purchaseOrder href with Purchase Order — use a distinct icon.
    if (/\binvoice\b/i.test(description)) {
        return 'pi pi-file-pdf';
    }

    if (!GeneralUtils.isNullOrEmpty(item.icon)) {
        const fromApi = normalizeIconClass(item.icon);
        if (fromApi) {
            return fromApi;
        }
    }

    if (item.id != null && MENU_ICON_BY_ID[item.id]) {
        return MENU_ICON_BY_ID[item.id];
    }

    const hrefKey = normalizeHrefKey(item.href);
    if (hrefKey && MENU_ICON_BY_HREF[hrefKey]) {
        return MENU_ICON_BY_HREF[hrefKey];
    }

    for (let i = 0; i < MENU_DESCRIPTION_RULES.length; i++) {
        const rule = MENU_DESCRIPTION_RULES[i];
        if (rule.pattern.test(description) || (hrefKey && rule.pattern.test(hrefKey))) {
            return rule.icon;
        }
    }

    return 'pi pi-th-large';
}

export function resolveMenuHref(item) {
    let href = item.href;
    if (!GeneralUtils.isNullOrEmpty(href) && href.indexOf("#/merchantUserSave?") >= 0) {
        href = "#/merchantUserSave?" + localStorage.getItem("merchantAccountId");
    }
    return href;
}

/** Friendly URL token → requisitionStatus enum */
export const PURCHASE_REQUISITION_STATUS_QUERY_MAP = {
    OPEN: 'OPEN',
    REQUISITION: 'OPEN',
    PURCHASEREQUISITION: 'OPEN',
    PURCHASE_ORDER: 'PURCHASE_ORDER',
    PURCHASEORDER: 'PURCHASE_ORDER',
    DELIVERY_NOTE: 'DELIVERY_NOTE',
    DELIVERYNOTE: 'DELIVERY_NOTE',
    RETURN: 'RETURN'
};

export function resolvePurchaseRequisitionStatusFromQuery(raw) {
    if (GeneralUtils.isNullOrEmpty(raw)) {
        return null;
    }
    let token = String(raw).trim();
    if (token.indexOf('=') >= 0) {
        token = token.split('=').pop();
    }
    token = token.split('&')[0].trim();
    const key = token.replace(/[-\s]/g, '').toUpperCase();
    return PURCHASE_REQUISITION_STATUS_QUERY_MAP[key] || null;
}

/**
 * Web-only: clone a single Purchase Requisition menu into status-scoped entries
 * (#/purchaseRequisition?DeliveryNote etc). Mobile menus are left unchanged.
 */
export function expandPurchaseRequisitionWebMenus(menus) {
    if (!Array.isArray(menus) || menus.length === 0) {
        return menus;
    }
    const variants = [
        {query: 'PurchaseRequisition', description: 'Purchase Requisition', icon: 'pi pi-list'},
        {query: 'PurchaseOrder', description: 'Purchase Order', icon: 'pi pi-shopping-cart'},
        {query: 'DeliveryNote', description: 'Delivery Note', icon: 'pi pi-inbox'},
        {query: 'Return', description: 'Return', icon: 'pi pi-replay'}
    ];
    const result = [];
    menus.forEach(item => {
        const href = String(item?.href || '');
        const hrefKey = normalizeHrefKey(href);
        const alreadyScoped = href.indexOf('?') >= 0;
        if (hrefKey !== 'purchaserequisition' || alreadyScoped) {
            result.push(item);
            return;
        }
        variants.forEach((variant, index) => {
            result.push({
                ...item,
                id: `${item.id}-pr-${variant.query}`,
                description: variant.description,
                href: `#/purchaseRequisition?${variant.query}`,
                icon: variant.icon,
                _prMenuCloneIndex: index
            });
        });
    });
    return result;
}

/**
 * Mobile-only: clone Purchase Requisition mobile menu into status-scoped entries
 * (#/purchaseRequisitionMobile?DeliveryNote etc). No Purchase Order on mobile.
 */
export function expandPurchaseRequisitionMobileMenus(menus) {
    if (!Array.isArray(menus) || menus.length === 0) {
        return menus;
    }
    const variants = [
        {query: 'PurchaseRequisition', description: 'Purchase Requisition Mobile', icon: 'pi pi-list'},
        {query: 'DeliveryNote', description: 'Delivery Note Mobile', icon: 'pi pi-inbox'},
        {query: 'Return', description: 'Return Mobile', icon: 'pi pi-replay'}
    ];
    const result = [];
    menus.forEach(item => {
        const href = String(item?.href || '');
        const hrefKey = normalizeHrefKey(href);
        const alreadyScoped = href.indexOf('?') >= 0;
        if (hrefKey !== 'purchaserequisitionmobile' || alreadyScoped) {
            result.push(item);
            return;
        }
        variants.forEach((variant, index) => {
            result.push({
                ...item,
                id: `${item.id}-prm-${variant.query}`,
                description: variant.description,
                href: `#/purchaseRequisitionMobile?${variant.query}`,
                icon: variant.icon,
                _prMenuCloneIndex: index
            });
        });
    });
    return result;
}
